import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { IoIosArrowDown, IoMdCheckmark } from "react-icons/io";
import { IoOptionsOutline } from "react-icons/io5";
import { courseCategoryLabelMap, ICourseCategory, ICourseType } from "utils/types";

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  font-family: "Manrope", sans-serif;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Tab = styled.button<{ active?: boolean }>`
  background: ${(props) => (props.active ? "#F6F8FF" : "none")};
  border: none;
  padding: ${(props) => (props.active ? "10px 16px" : "10px")};
  font-size: 14px;
  cursor: pointer;
  color: ${(props) => (props.active ? "#5575F7" : "#6B7280")};
  border-radius: 10px;
  font-weight: ${(props) => (props.active ? "500" : "400")};
  white-space: nowrap;
  font-family: "Manrope", sans-serif;
  transition: all 0.2s ease;

  &:hover {
    color: #5575f7;
    background: ${(props) => (!props.active ? "#F9FAFB" : "")};
  }
`;

const DropdownContainer = styled.div`
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownButton = styled.button`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 16px;
  font-size: 14px;
  color: #6b7280;
  font-family: "Manrope", sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-radius: 8px;
  min-width: 150px;

  &:hover {
    border-color: #5575f7;
    color: #5575f7;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 200px;
  padding: 0.5rem 0;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  z-index: 10;
  border: 1px solid #e5e7eb;
`;

const DropdownItem = styled.button<{ active?: boolean }>`
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  background: ${(props) => (props.active ? "#F6F8FF" : "none")};
  border: none;
  font-size: 14px;
  font-family: "Manrope", sans-serif;
  color: ${(props) => (props.active ? "#5575F7" : "#374151")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #5575f7;
  }
`;

// Mobile only — single "Filters" dropdown grouping all course filters
const MobileFilterContainer = styled.div`
  position: relative;
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 100%;
  }
`;

const MobileFilterButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border: 1px solid #e5e7eb;
  padding: 10px 16px;
  font-size: 14px;
  color: #374151;
  font-family: "Manrope", sans-serif;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #5575f7;
    color: #5575f7;
  }
`;

const MobileFilterButtonLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MobileFilterMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 0.5rem;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  z-index: 20;
  border: 1px solid #e5e7eb;
`;

const MobileGroupLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  padding: 0.5rem 0.75rem 0.25rem;
`;

const MobileFilterItem = styled.button<{ active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.75rem;
  background: ${(props) => (props.active ? "#F6F8FF" : "none")};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: ${(props) => (props.active ? "#5575F7" : "#374151")};
  font-weight: ${(props) => (props.active ? "500" : "400")};
  font-family: "Manrope", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #5575f7;
  }
`;

export type ICourseTypeTab = ICourseType | "all";
export type ICourseCategoryTab = ICourseCategory | "all";
const courseTypeTabs: {id: ICourseTypeTab, label: string}[] = [
  { id: "all", label: "All courses" },
  // { id: "premium", label: "Premium courses" },
  { id: "free", label: "Free courses" },
]
const courseCategoryTab: {id: ICourseCategoryTab, label: string}[] = [
  { id: "all", label: "All" },
  { id: "medicalExams", label: courseCategoryLabelMap("medicalExams") },
  // { id: "interview", label: "Interviews" },
  { id: "license", label: courseCategoryLabelMap("license") },
  { id: "others", label: courseCategoryLabelMap("others") },
]

export interface IFilter {
  courseType: ICourseTypeTab;
  courseCategoryTab: ICourseCategoryTab;
}
export interface FilterBarProps {
  onSetFilter: React.Dispatch<React.SetStateAction<IFilter>>
  filter: IFilter;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filter, onSetFilter,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        mobileFilterRef.current &&
        !mobileFilterRef.current.contains(event.target as Node)
      ) {
        setIsMobileFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <FilterContainer>
      <FilterTabs>
        {courseCategoryTab.map((tab) => (
          <Tab
            active={tab.id === filter.courseCategoryTab}
            key={tab.id}
            onClick={() => onSetFilter({ ...filter, courseCategoryTab: tab.id })}
          >
            {tab.label}
          </Tab>
        ))}
      </FilterTabs>

      <DropdownContainer ref={dropdownRef}>
        <DropdownButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          {courseTypeTabs.find((tab) => tab.id === filter.courseType)?.label || "All"}
          <IoIosArrowDown
            size={16}
            style={{
              transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </DropdownButton>

        <DropdownMenu isOpen={isDropdownOpen}>
          {courseTypeTabs.map((tab) => (
            <DropdownItem
              active={tab.id === filter.courseType}
              key={tab.id}
              onClick={() => {
                onSetFilter({ ...filter, courseType: tab.id });
                setIsDropdownOpen(false);
              }}
            >
              {tab.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </DropdownContainer>

      <MobileFilterContainer ref={mobileFilterRef}>
        <MobileFilterButton onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}>
          <MobileFilterButtonLabel>
            <IoOptionsOutline size={18} />
            Filters
          </MobileFilterButtonLabel>
          <IoIosArrowDown
            size={16}
            style={{
              transform: isMobileFilterOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </MobileFilterButton>

        <MobileFilterMenu isOpen={isMobileFilterOpen}>
          <MobileGroupLabel>Category</MobileGroupLabel>
          {courseCategoryTab.map((tab) => (
            <MobileFilterItem
              active={tab.id === filter.courseCategoryTab}
              key={tab.id}
              onClick={() => onSetFilter({ ...filter, courseCategoryTab: tab.id })}
            >
              <span>{tab.label}</span>
              {tab.id === filter.courseCategoryTab && <IoMdCheckmark size={18} />}
            </MobileFilterItem>
          ))}
          <MobileGroupLabel>Course type</MobileGroupLabel>
          {courseTypeTabs.map((tab) => (
            <MobileFilterItem
              active={tab.id === filter.courseType}
              key={tab.id}
              onClick={() => onSetFilter({ ...filter, courseType: tab.id })}
            >
              <span>{tab.label}</span>
              {tab.id === filter.courseType && <IoMdCheckmark size={18} />}
            </MobileFilterItem>
          ))}
        </MobileFilterMenu>
      </MobileFilterContainer>
    </FilterContainer>
  );
};